/**
 * Data layer for `multi-currency`. Thin typed fetch wrappers over /api/v1/multi-currency.
 * Raw `fetch()` is confined to this file per the dash contract.
 */

export interface CurrencyRow {
	id: string;
	tenant_id: string;
	code: string;
	name: string;
	symbol: string;
	rate_vs_usd: number;
	last_refreshed_at: string | null;
	is_active: boolean;
	auto_refresh: boolean;
	created_at: number;
	updated_at: number;
}

export interface ListCurrencyResponse {
	count: number;
	items: CurrencyRow[];
}

export interface CreateCurrencyBody {
	code: string;
	name?: string;
	symbol?: string;
	rate_vs_usd?: number;
	auto_refresh?: boolean;
}

export interface UpdateCurrencyBody {
	is_active?: boolean;
	auto_refresh?: boolean;
	rate_vs_usd?: number;
}

export interface RefreshRatesResponse {
	refreshed: number;
	last_refreshed_at: string;
}

const BASE = "/api/v1/multi-currency";

async function parseOrThrow<T>(res: Response): Promise<T> {
	const text = await res.text();
	let data: unknown = null;
	if (text) {
		try {
			data = JSON.parse(text);
		} catch {
			data = text;
		}
	}
	if (!res.ok) {
		const msg =
			data &&
			typeof data === "object" &&
			data !== null &&
			"error" in data &&
			typeof (data as { error: unknown }).error === "string"
				? (data as { error: string }).error
				: `HTTP ${res.status}`;
		throw new Error(msg);
	}
	return data as T;
}

export async function list(): Promise<ListCurrencyResponse> {
	const res = await fetch(BASE, {
		method: "GET",
		credentials: "include",
		headers: { accept: "application/json" },
	});
	return parseOrThrow<ListCurrencyResponse>(res);
}

export async function create(body: CreateCurrencyBody): Promise<CurrencyRow> {
	const res = await fetch(BASE, {
		method: "POST",
		credentials: "include",
		headers: {
			accept: "application/json",
			"content-type": "application/json",
		},
		body: JSON.stringify(body),
	});
	return parseOrThrow<CurrencyRow>(res);
}

export async function update(
	id: string,
	body: UpdateCurrencyBody,
): Promise<CurrencyRow> {
	const res = await fetch(`${BASE}/${encodeURIComponent(id)}`, {
		method: "PATCH",
		credentials: "include",
		headers: {
			accept: "application/json",
			"content-type": "application/json",
		},
		body: JSON.stringify(body),
	});
	return parseOrThrow<CurrencyRow>(res);
}

export async function remove(id: string): Promise<{ removed: boolean }> {
	const res = await fetch(`${BASE}/${encodeURIComponent(id)}`, {
		method: "DELETE",
		credentials: "include",
		headers: { accept: "application/json" },
	});
	return parseOrThrow<{ removed: boolean }>(res);
}

export async function refreshAll(): Promise<RefreshRatesResponse> {
	const res = await fetch(`${BASE}/refresh`, {
		method: "POST",
		credentials: "include",
		headers: {
			accept: "application/json",
			"content-type": "application/json",
		},
		body: JSON.stringify({}),
	});
	return parseOrThrow<RefreshRatesResponse>(res);
}

export const multiCurrencyQueryKeys = {
	all: ["multi-currency"] as const,
	list: () => [...multiCurrencyQueryKeys.all] as const,
	detail: (id: string) => [...multiCurrencyQueryKeys.all, id] as const,
};
